/**
 * ConversionTrackerController — tracker propriétaire `conversion_event`
 * (EPIC-038 / 38.4 — §2.K).
 *
 * Controller global monté sur le <body> des layouts app / auth / blank.
 * Deux responsabilités :
 *
 *  1. Amorcer un `session_id` de visite dans `sessionStorage`
 *     (`crypto.randomUUID()`, régénéré à chaque visite — pas de cookie,
 *     pas de `localStorage`, pas de fingerprinting, cf. §2.K « régénéré
 *     à chaque visite »).
 *  2. Exposer `window.NotamicroTracker` : 3 helpers stateless que
 *     n'importe quel controller métier appelle pour émettre un event de
 *     funnel sans réimplémenter le transport.
 *
 * `window.NotamicroTracker` est assigné dès l'évaluation du module —
 * donc avant le `connect()` du moindre controller. Les events `*_started`
 * partent depuis le `connect()` d'autres controllers : l'objet doit être
 * disponible immédiatement, sans dépendre de l'ordre de montage Stimulus.
 *
 * Transport : POST `fetch` brut vers `/conversion-events` avec
 * `keepalive: true` (events émis juste avant une navigation) et
 * `credentials: 'include'` (le backend hydrate `user_id` /
 * `organization_id` depuis le cookie JWT s'il est présent). Tout échec
 * est avalé (`console.warn`) — l'analytics ne doit jamais casser l'UX.
 */

import BaseController from "./base_controller.js";
import { API_BASE_URL } from "../services/api.js";
import { getSessionId } from "../utils/session.js";

/**
 * POST un event vers l'endpoint tracker. Échec silencieux : aucune
 * exception ne remonte, l'UX n'est jamais interrompue. Couvre aussi le
 * cas où `sessionStorage` est indisponible (navigation privée stricte).
 */
function sendEvent(eventType, context = {}) {
  try {
    fetch(`${API_BASE_URL}/conversion-events`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({
        eventType,
        context: context ?? {},
        sessionId: getSessionId(),
      }),
    }).catch((err) => console.warn("[conversion-tracker] event ignoré", eventType, err));
  } catch (err) {
    console.warn("[conversion-tracker] event ignoré", eventType, err);
  }
}

/**
 * API publique du tracker — assignée au chargement du module pour être
 * disponible avant tout `connect()`.
 */
window.NotamicroTracker = {
  /** Émet un event arbitraire de la liste enum `conversion_event_type`. */
  track: (eventType, context = {}) => sendEvent(eventType, context),
  /** Raccourci : modale de restriction affichée (contexte `{ action }`). */
  trackRestrictedModalShown: (action) => sendEvent("restricted_modal_shown", { action }),
  /** Raccourci : clic CTA de la modale — `target` ∈ {'activate','continue'}. */
  trackRestrictedCta: (target) => sendEvent(
    target === "activate" ? "restricted_modal_cta_activate" : "restricted_modal_cta_continue",
    {},
  ),
};

export default class ConversionTrackerController extends BaseController {
  connect() {
    // Amorçage explicite du session_id dès le montage (cf. §2.K) — le
    // transport le ré-amorce de toute façon paresseusement au besoin.
    this.sessionId = getSessionId();
  }
}
